import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

import {
    BarChart3,
    Heart,
    AlertTriangle,
    Search,
    Database,
    MessageSquare,
    Activity,
    ChevronLeft,
    TrendingUp,
    Globe2,
    Sparkles,
    Target,
    Zap,
    TrendingDown,
    CheckCircle2,
    Award,
    ArrowUp, // for charts
    ArrowDown, // for charts
    PieChartIcon,
    Mail,
    Clock,
    XCircle,
    Download,
    Settings
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import RenderDetailView from '../RenderDetailView';
import ApiService from '../../services/ApiService';
import DateRangePicker from '../ui/DatePicker';
import { BarChart, PieChart, LineChart } from '@mui/x-charts';
import Stack from '@mui/material/Stack';
import { PiecewiseColorLegend } from '@mui/x-charts/ChartsLegend';
import BarLabelAtBase from '../BarLabelAtBase';
import BarShadedBackground from '../BarShadedBackground';
import ChartFooter from '../ui/ChartFooter';
import { DESCRIPTIONS } from '../../utils/descriptions';
import InfoTooltip from '../ui/InfoTooltip';
import { Paper, Typography } from '@mui/material';
import QueryPreviewModal from '../ui/QueryPreviewModal';
import CategoryBreakdownModal from '../ui/CategoryBreakdownModal';
import ReportSettingsModal from '../ui/ReportSettingsModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
    StatusCardSkeleton,
    KeyTakeawaySkeleton,
    ChartSkeleton,
    BarChartSkeleton,
    LineChartSkeleton,
    RecommendationSkeleton
} from '../ui/ReportSkeletons';

// Sort Button Component
const SortButton = ({ sortOrder, onSort }) => {
    return (
        <button
            onClick={onSort}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            title={`Sort ${sortOrder === 'desc' ? 'ascending' : 'descending'}`}
        >
            {sortOrder === 'desc' ? (
                <>
                    <ArrowDown className="h-3.5 w-3.5" />
                    <span>High to Low</span>
                </>
            ) : (
                <>
                    <ArrowUp className="h-3.5 w-3.5" />
                    <span>Low to High</span>
                </>
            )}
        </button>
    );
};

// Helper to sort chart data
const sortChartData = (data, sortOrder) => {
    if (!data) return [];
    return [...data].sort((a, b) => {
        if (sortOrder === 'desc') {
            return b.value - a.value;
        }
        return a.value - b.value;
    });
};

// Utility function to format category names
const formatLabel = (str) => {
    if (!str) return 'N/A';
    return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getEmotionEmoji = (emotion) => {
    const emotionEmojis = {
        // Positive emotions
        'happy': '😊', 'excited': '🎉', 'joy': '😄', 'love': '❤️', 'satisfied': '😌',
        'grateful': '🙏', 'appreciation': '🙏', 'proud': '😎', 'relief': '😮‍💨',
        'calm': '😌', 'content': '☺️', 'enthusiastic': '🤩', 'optimistic': '😃',
        'hopeful': '🌟', 'amused': '😄', 'delighted': '😁',
        // Negative emotions
        'angry': '😠', 'frustrated': '😤', 'sad': '😢', 'disappointed': '😞',
        'upset': '😔', 'annoyed': '😒', 'irritated': '😑', 'furious': '🤬',
        'hurt': '💔', 'depressed': '😭', 'miserable': '😣', 'bitter': '😖',
        'resentful': '😾',
        // Anxiety/Fear related
        'anxious': '😰', 'anxiety': '😰', 'worried': '😟', 'nervous': '😬',
        'stressed': '😓', 'overwhelmed': '😵', 'fearful': '😨', 'scared': '😱',
        'panicked': '😰', 'tense': '😬',
        // Confusion/Uncertainty
        'confused': '😕', 'uncertain': '🤔', 'doubtful': '🤨', 'skeptical': '🤨',
        'puzzled': '😵‍💫', 'perplexed': '😵', 'bewildered': '😳',
        // Neutral
        'neutral': '😐', 'indifferent': '😶', 'bored': '😑', 'tired': '😴',
        // Surprise
        'surprised': '😲', 'shocked': '😱', 'amazed': '😮', 'astonished': '🤯',
        // Urgency/Action
        'urgent': '⚡', 'impatient': '⏰', 'rushed': '🏃',
        // Other
        'curious': '🤔', 'interested': '👀', 'disgusted': '🤢', 'embarrassed': '😳',
        'shame': '😳', 'guilty': '😔', 'lonely': '😔', 'jealous': '😒', 'envious': '😤'
    };
    const normalizedEmotion = emotion?.toLowerCase().trim();
    return emotionEmojis[normalizedEmotion] || '💭';
};


// Empty State Component
const EmptyChart = ({ icon: Icon, title, description }) => (
    <div className="flex flex-col items-center justify-center h-64 text-center px-4">
        <div className="relative">
            <div className="absolute inset-0 bg-gray-200 rounded-full blur-xl opacity-20 animate-pulse"></div>
            <div className="relative p-6 bg-gray-50 rounded-2xl mb-4 transform hover:scale-105 transition-transform duration-300">
                <Icon className="h-10 w-10 text-gray-400" />
            </div>
        </div>
        <h4 className="text-lg font-bold text-gray-900 mb-2">{title}</h4>
        <p className="text-sm text-gray-500 max-w-sm leading-relaxed">{description}</p>
    </div>
);

// Compact Overview Card

// Section Header
const SectionHeader = ({ title, description, icon: Icon }) => {
    return (
        <div className="flex items-start gap-4 mb-3 mt-4">
            <div className={`p-3 rounded-xl bg-black text-white shadow-sm`}>
                <Icon className="h-6 w-6" />
            </div>
            <div>
                <h2 className="text-xl font-bold text-gray-900">{title}</h2>
                <p className="text-sm text-gray-600">{description}</p>
            </div>
        </div>
    );
};

// Report Header Component
const ReportHeader = ({ orgData, startDate, endDate }) => {
    if (!orgData) return null;

    const params = new URLSearchParams(window.location.search);
    const reportType = params.get('type') || 'weekly';

    const formatHeaderDate = () => {
        if (reportType === 'hourly') {
            return `${dayjs(startDate).format('HH:mm')} – ${dayjs(endDate).format('HH:mm, MMM DD, YYYY')}`;
        }

        return `${dayjs(startDate).format('MMM DD')} – ${dayjs(endDate).format('MMM DD, YYYY')}`;
    };

    return (
        <div id="report-header" className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm mb-4 flex justify-between items-start">
            <div className="flex flex-col gap-2">
                <h2 className="text-2xl font-bold text-gray-900">{orgData.organization_name || 'Organization Name'}</h2>
                <div className="space-y-1 text-sm text-gray-600">
                    <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {orgData.organization_email || 'support@example.com'}
                    </p>
                    <p className="flex items-center gap-2">
                        <Globe2 className="w-4 h-4 text-gray-400" />
                        {orgData.organization_domain || 'example.com'}
                    </p>
                </div>
                <div className="mt-2 text-xs text-gray-500 font-medium">
                    Report Period: {formatHeaderDate()}
                </div>
            </div>
            <div className="flex flex-col items-end">
                <img
                    src="/logo/uttik_logo_b.png"
                    alt="Uttik Logo"
                    className="h-8 w-auto object-contain mb-2"
                />
                <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">Powered by Uttik</span>
            </div>
        </div>
    );
};

// StatusCard component from Overview.js
const StatusCard = ({ icon: IconComponent, title, value, onClick }) => {
    return (
        <div
            className={`group relative bg-white/80 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1 ${onClick ? 'cursor-pointer' : ''}`}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
        >
            <div className="absolute inset-0 bg-gray-50/50"></div>
            <div className="relative p-3 sm:p-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className={`flex-shrink-0 p-2 sm:p-2.5 bg-black rounded-lg shadow-lg group-hover:scale-110 transition-transform duration-200`}>
                        <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1 flex flex-col items-start justify-center">
                        <dt className="text-xs font-medium text-gray-600 mb-0.5 text-left block break-words" title={title}>
                            {title}
                        </dt>
                        <dd className="text-lg sm:text-xl font-bold text-gray-900 w-full text-left block break-all">
                            {value}
                        </dd>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Key Takeaways Section
const KeyTakeaways = ({ insights = [], onInsightClick }) => {
    if (!insights || insights.length === 0) return null;

    return (
        <div>
            <SectionHeader
                title="Key Takeaways & Action Items"
                description="Critical insights based on current data."
                icon={Target}
                color="purple"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {insights.map((insight, index) => {
                    const Icon = insight.icon;
                    const isClickable = !!insight.filterType;

                    return (
                        <div
                            key={index}
                            onClick={() => isClickable && onInsightClick && onInsightClick(insight)}
                            className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-all duration-200 ${isClickable ? 'cursor-pointer hover:scale-[1.01] active:scale-[0.99]' : ''}`}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className={`p-2 rounded-lg bg-gray-50 shadow-sm text-black`}>
                                    <Icon className="h-6 w-6" />
                                </div>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold bg-black text-white`}>
                                    {insight.type.toUpperCase()}
                                </span>
                            </div>

                            <h3 className="text-lg font-bold text-gray-900 mb-1">{insight.title}</h3>
                            <p className="font-semibold text-gray-900 mb-2">{insight.value}</p>
                            <p className="text-sm text-gray-600 mb-4">{insight.description}</p>

                            {insight.samples && insight.samples.length > 0 && (
                                <div className="bg-white/60 rounded-lg p-3 mb-4">
                                    <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Sample Queries:</p>
                                    <ul className="space-y-1.5">
                                        {insight.samples.map((sample, idx) => (
                                            <li key={idx} className="text-xs text-gray-700 italic border-l-2 border-gray-300 pl-2">
                                                {sample}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex items-center gap-2 pt-4 border-t border-gray-200/50">
                                <Zap className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">
                                    <span className="font-bold">Action:</span> {insight.action}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Recommendations Summary Section
const RecommendationsSummary = ({ recommendations = [] }) => {
    if (!recommendations || recommendations.length === 0) return null;

    return (
        <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm mt-4">
            <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-black rounded-lg shadow-md">
                    <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-gray-900">Recommendations Summary</h3>
                    <p className="text-sm text-gray-600">Prioritized action plan based on current insights.</p>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                <table id="recommendations-summary-table" className="w-full border-collapse min-w-[640px]">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Priority</th>
                            <th scope="col" className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Action Item</th>
                            <th scope="col" className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">Expected Impact</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {recommendations.map((rec, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${rec.priority === 'P0' ? 'bg-black text-white' :
                                        rec.priority === 'P1' ? 'bg-gray-800 text-white' :
                                            'bg-gray-200 text-gray-900'
                                        }`}>
                                        {rec.priority}
                                    </span>
                                </td>
                                <td className="px-3 sm:px-6 py-4 sm:py-6 text-sm font-bold text-gray-900 align-top break-words leading-relaxed border-b border-gray-50">
                                    {rec.action}
                                </td>
                                <td className="px-3 sm:px-6 py-4 sm:py-6 text-sm font-medium text-gray-600 align-top break-words leading-relaxed border-b border-gray-50">
                                    {rec.impact}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

function Reports() {
    const navigate = useNavigate();
    const [selectedReport, setSelectedReport] = useState(null);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [hasFilter, setHasFilter] = useState(false);
    const [dateParams, setDateParams] = useState({});
    const [rootCause, setRootCause] = useState(null);
    const [queryClassification, setQueryClassification] = useState(null);
    const [urgency, setUrgency] = useState(null);
    const [ragPerformance, setRagPerformance] = useState(null);
    const [sentiment, setSentiment] = useState(null);
    const [overview, setoverview] = useState(null);
    const [dailyTraffic, setDailyTraffic] = useState([]);
    const [queryTechnicals, setQueryTechnicals] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [orgData, setOrgData] = useState(null);
    const [isAutoSending, setIsAutoSending] = useState(false);

    const [keyInsights, setKeyInsights] = useState([]);
    const [keyRecommendations, setKeyRecommendations] = useState([]);

    const generateInsights = () => {
        if (!overview) return;

        const newInsights = [];
        const newRecommendations = [];

        // 0. Default Insights if data is sparse (ensures we have enough for 3)
        const defaultInsights = [
            {
                type: 'info',
                title: 'Data Collection Active',
                value: 'Monitoring Traffic',
                description: 'Our AI is actively analyzing incoming support queries to identify patterns.',
                action: 'Check back regularly for new strategic insights.',
                icon: Activity,
                color: 'blue'
            },
            {
                type: 'info',
                title: 'Quality Assurance',
                value: 'RAG Performance',
                description: 'We are monitoring the quality of AI responses against your knowledge base.',
                action: 'Update Knowledge Vault periodically for best results.',
                icon: Database,
                color: 'purple'
            },
            {
                type: 'info',
                title: 'Sentiment Tracking',
                value: 'Customer Pulse',
                description: 'We track customer emotions across all interactions to ensure satisfaction.',
                action: 'Review "Sentiment Details" for emotional trends.',
                icon: Heart,
                color: 'green'
            }
        ];

        // 1. Backlog Insight
        const total = overview.total_queries || 0;
        const pending = overview.pending_queries || 0;
        const backlogPct = total > 0 ? (pending / total) * 100 : 0;

        if (backlogPct > 10 || pending > 50) { // Threshold
            newInsights.push({
                type: 'critical',
                title: `CRITICAL: ${backlogPct.toFixed(1)}% Follow-up Backlog`,
                value: `${pending} queries pending follow-up`,
                description: 'Immediate action required to prevent customer churn.',
                action: 'Assign dedicated team to clear backlog within 48 hours.',
                icon: AlertTriangle,
                color: 'red'
            });
            newRecommendations.push({
                priority: 'P0',
                action: `Clear ${pending} follow-up backlog within 48hrs`,
                impact: 'Reduce negative sentiment 20-30%'
            });
        }

        // 2. Sentiment Insight
        if (sentiment && sentiment.overall) {
            const negative = sentiment.overall.find(s => s.id === 'negative')?.value || 0;
            const totalSentiment = sentiment.overall.reduce((sum, s) => sum + s.value, 0);
            const negPct = totalSentiment > 0 ? (negative / totalSentiment) * 100 : 0;

            if (negPct > 15) {
                newInsights.push({
                    type: 'negative',
                    title: `${negPct.toFixed(0)}% Negative Sentiment`,
                    value: 'Requires Attention',
                    description: `Customer frustration driven by issues (${negative} instances).`,
                    action: 'Implement auto-acknowledgment + 24hr response SLA.',
                    icon: TrendingDown,
                    color: 'orange'
                });

                newRecommendations.push({
                    priority: 'P1',
                    action: 'Implement 24hr response SLA',
                    impact: 'Improve CSAT by 25-35%'
                });
            }
        }

        // 3. Gap Insight
        if (ragPerformance && ragPerformance.knowledgeGaps && ragPerformance.knowledgeGaps.length > 0) {
            const topGap = ragPerformance.knowledgeGaps[0];
            newInsights.push({
                type: 'gap',
                title: `Top Content Gap: ${formatLabel(topGap.label)}`,
                value: `${topGap.value} queries`,
                description: 'Users are asking about this topic but finding limited info.',
                action: 'Create comprehensive spec comparison page + FAQ optimization.',
                icon: Search,
                color: 'blue',
                filterType: 'knowledge_gap',
                filterValue: topGap.id,
                modalTitle: `Content Gap Queries: ${formatLabel(topGap.label)}`
            });
            newRecommendations.push({
                priority: 'P0',
                action: `Create content for ${formatLabel(topGap.label)}`,
                impact: 'Reduce info requests by 40%'
            });
        }

        // 4. Urgency/Critical
        if (urgency && urgency.criticalCount > 0) {
            newRecommendations.push({
                priority: 'P1',
                action: 'Review critical escalation protocol',
                impact: `Address ${urgency.criticalCount} critical issues`
            });
        }

        // Add generic recommendation if list is short or empty
        if (newRecommendations.length < 3) {
            newRecommendations.push({
                priority: 'P2',
                action: 'Build self-service order management',
                impact: 'Deflect 35-40% of action requests'
            });
            newRecommendations.push({
                priority: 'P2',
                action: 'Firmware troubleshooting knowledge base',
                impact: 'Reduce problem reports 15-20%'
            });
        }

        // Fill up to 3 insights using defaults if needed
        while (newInsights.length < 3 && defaultInsights.length > 0) {
            const defaultItem = defaultInsights.shift();
            // Check if this default item's title is already covered or similar
            if (!newInsights.some(ins => ins.title.includes(defaultItem.title))) {
                newInsights.push(defaultItem);
            }
        }

        setKeyInsights(newInsights.slice(0, 3));
        setKeyRecommendations(newRecommendations);
    };

    useEffect(() => {
        if (overview && sentiment && ragPerformance && urgency) {
            generateInsights();
        }
    }, [overview, sentiment, ragPerformance, urgency]);

    // --- NEW SORTING STATE ---

    const [primaryCategorySortOrder, setPrimaryCategorySortOrder] = useState('desc');
    const [intentSortOrder, setIntentSortOrder] = useState('desc');
    const [rootCauseSortOrder, setRootCauseSortOrder] = useState('desc');
    const [urgencySortOrder, setUrgencySortOrder] = useState('desc');
    const [ragGapSortOrder, setRagGapSortOrder] = useState('desc');
    const [impactSortOrder, setImpactSortOrder] = useState('desc');
    const [emotionSortOrder, setEmotionSortOrder] = useState('desc');
    const [sentimentSortOrder, setSentimentSortOrder] = useState('desc');
    const [languageSortOrder, setLanguageSortOrder] = useState('desc');

    // Category Modal state
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [categoryModalData, setCategoryModalData] = useState([]);
    const [categoryModalTitle, setCategoryModalTitle] = useState('');

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalQueries, setModalQueries] = useState([]);
    const [modalTitle, setModalTitle] = useState('');
    const [modalFilterType, setModalFilterType] = useState('');
    const [modalFilterValue, setModalFilterValue] = useState('');
    const [modalLoading, setModalLoading] = useState(false);
    const [modalPagination, setModalPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total_items: 0,
        has_next: false,
        has_previous: false
    });

    const fetchModalQueries = async (page, filterType, filterValue) => {
        setModalLoading(true);

        try {
            const payload = {
                page: page,
                per_page: 10,
                filters: {}
            };

            if (startDate && endDate) {
                payload.start_date = startDate.toISOString().split('T')[0];
                payload.end_date = endDate.toISOString().split('T')[0];
            }

            if (filterType === 'root_cause_category') {
                payload.filters.root_cause = { category: [filterValue] };
            } else if (filterType === 'primary_category') {
                payload.filters.classification = { primary_category: [filterValue] };
            } else if (filterType === 'intent') {
                payload.filters.classification = { intent: [filterValue] };
            } else if (filterType === 'urgency_level') {
                payload.filters.urgency = { level: [filterValue.toLowerCase()] };
            } else if (filterType === 'sentiment_overall') {
                payload.filters.sentiment = { sentiment: [filterValue.toLowerCase()] };
            } else if (filterType === 'emotion') {
                payload.filters.sentiment = { emotion: [filterValue.toLowerCase()] };
            } else if (filterType === 'knowledge_gap') {
                payload.filters.rag = { knowledge_gap_category: [filterValue] };
            } else if (filterType === 'business_impact') {
                payload.filters.urgency = { business_impact: [filterValue.toLowerCase()] };
            } else if (filterType === 'product_area') {
                // Use search_query for product area as requested to get more relevant results
                payload.filters.search_query = filterValue;
            }

            const response = await ApiService.getSupportQueries(payload);

            let queriesData = [];
            if (response.success && response.data) {
                queriesData = response.data;
            } else if (Array.isArray(response)) {
                queriesData = response;
            } else if (response.queries) {
                queriesData = response.queries;
            }

            if (response.pagination) {
                setModalPagination(response.pagination);
            }

            if (queriesData && queriesData.length > 0) {
                setModalQueries(queriesData);
            } else {
                setModalQueries([]);
                if (page === 1) toast.info('No queries found for this filter');
            }
        } catch (error) {
            console.error('Failed to fetch queries:', error);
            toast.error('Failed to load queries');
            setModalQueries([]);
        } finally {
            setModalLoading(false);
        }
    };

    const handleModalPageChange = (newPage) => {
        fetchModalQueries(newPage, modalFilterType, modalFilterValue);
    };

    const handleChartClick = (filterType, filterValue, title) => {
        setModalOpen(true);
        setModalTitle(title);
        setModalFilterType(filterType);
        setModalFilterValue(filterValue);
        setModalQueries([]);
        fetchModalQueries(1, filterType, filterValue);
    };

    const getProcessedProductAreaData = () => {
        if (!queryClassification?.productArea) return [];

        // Always sort by value desc for Top 10 calculation
        const sorted = [...queryClassification.productArea].sort((a, b) => b.value - a.value);

        if (sorted.length <= 10) return sorted;

        const top10 = sorted.slice(0, 10);
        const other = sorted.slice(10);
        const otherValue = other.reduce((sum, item) => sum + item.count || sum + item.value, 0); // Handle potentially missing value prop if count used

        return [
            ...top10,
            {
                id: 'other_categories',
                label: 'Other Categories',
                value: otherValue,
                isOther: true,
                fullList: other
            }
        ];
    };

    const handleProductAreaClick = (item) => {
        if (item.isOther) {
            setCategoryModalTitle('Other Product Areas');
            setCategoryModalData(item.fullList);
            setCategoryModalOpen(true);
        } else {
            handleChartClick(
                'product_area',
                item.label, // Use label for text search
                `Product Area: ${item.label}`
            );
        }
    };

    const loadOverview = async (params = {}) => {
        try {
            if (Object.keys(params).length === 0) {
                setLoading(true);
            }
            const data = await ApiService.reportOverview(params);
            const dashboardData = await ApiService.getDashboardOverview(params);
            const dashboardAnalytics = await ApiService.getDashboardAnalytics(params);

            console.log('Report Overview Data:', data);

            if (dashboardData) {
                setoverview(dashboardData);
            }

            if (dashboardAnalytics && dashboardAnalytics.analytics) {
                setDailyTraffic(dashboardAnalytics.analytics.daily_traffic || []);
            }

            // Load organization data
            try {
                const orgInfo = await ApiService.getOrganizationData();
                setOrgData(orgInfo);
            } catch (err) {
                console.error('Failed to load org data:', err);
            }

            if (data && data.analytics) {
                // setoverview(data.analytics.overview || null);
                // setDailyTraffic(data.analytics.daily_traffic || []);
            }

            //root causes
            const response = await ApiService.rootCause(params);

            if (response.success && response.analytics) {
                const { category_distribution, root_cause_identification_avg_confidence } = response.analytics;

                const rootCause = {
                    category: category_distribution?.map(item => ({
                        id: item.root_cause_category,
                        label: formatLabel(item.root_cause_category),
                        value: item.count
                    })) || [],
                    avgConfidence: Math.round(root_cause_identification_avg_confidence * 100) || 0,
                    totalCauses: category_distribution?.length || 0,
                    topCause: formatLabel(category_distribution?.[0]?.root_cause_category),
                    topCausePercentage: category_distribution?.length > 0
                        ? Math.round((category_distribution[0].count / category_distribution.reduce((sum, item) => sum + item.count, 0)) * 100)
                        : 0,
                    dateRange: response.analytics.date_range
                }
                setRootCause(rootCause)
            }

            // query classification
            const responseClassification = await ApiService.queryClassification(params);
            console.log('Query Classification Response:', responseClassification);

            if (responseClassification.success && responseClassification.analytics) {
                const { primary_category_distribution, intent_distribution, product_area_distribution } = responseClassification.analytics;

                const queryClassification = {
                    primaryCategory: primary_category_distribution?.map(item => ({
                        id: item.primary_category,
                        label: formatLabel(item.primary_category),
                        value: item.count
                    })) || [],
                    productArea: product_area_distribution?.map(item => ({
                        id: item.product_area,
                        label: formatLabel(item.product_area),
                        value: item.count
                    })) || [],
                    intent: intent_distribution?.map(item => ({
                        id: item.intent,
                        label: formatLabel(item.intent),
                        value: item.count
                    })) || [],
                    totalQueries: primary_category_distribution?.reduce((sum, item) => sum + item.count, 0) || 0,
                    topCategory: formatLabel(primary_category_distribution?.[0]?.primary_category),
                    topIntent: formatLabel(intent_distribution?.[0]?.intent),
                    dateRange: responseClassification.analytics.date_range
                }
                setQueryClassification(queryClassification)
            }


            //urgency
            const responseUrgency = await ApiService.urgency(params);

            if (responseUrgency.success && responseUrgency.analytics) {
                const { urgency_level_distribution, business_impact_distribution, average_urgency_score, urgency_score_distribution } = responseUrgency.analytics;

                const urgency = {
                    level: urgency_level_distribution?.map(item => ({
                        id: item.urgency_level,
                        label: formatLabel(item.urgency_level),
                        value: item.count
                    })) || [],
                    businessImpact: business_impact_distribution?.map(item => ({
                        id: item.business_impact,
                        label: formatLabel(item.business_impact),
                        value: item.count
                    })) || [],
                    averageScore: average_urgency_score || 0,
                    scoreDistribution: urgency_score_distribution?.data?.map(item => ({
                        label: `${item.range[0]}-${item.range[1]}`,
                        value: item.count
                    })) || [],
                    highUrgency: urgency_level_distribution?.find(u => u.urgency_level === 'high')?.count || 0,
                    criticalCount: urgency_level_distribution?.find(u => u.urgency_level === 'critical')?.count || 0,
                    totalCount: urgency_level_distribution?.reduce((sum, item) => sum + item.count, 0) || 0,
                    dateRange: responseUrgency.analytics.date_range
                }

                setUrgency(urgency)
            }

            // rag performance
            const responseRag = await ApiService.ragPerformance(params);
            if (responseRag.success && responseRag.analytics) {
                const { context_quality_distribution, knowledge_gap_category_distribution, average_rag_performance_score, rag_performance_score_distribution } = responseRag.analytics;

                const rag = {
                    contextQuality: context_quality_distribution?.map(item => ({
                        id: item.context_quality,
                        label: formatLabel(item.context_quality),
                        value: item.count
                    })) || [],
                    knowledgeGaps: knowledge_gap_category_distribution?.map(item => ({
                        id: item.category,
                        label: formatLabel(item.category),
                        value: item.count
                    })) || [],
                    averageScore: average_rag_performance_score || 0,
                    scoreDistribution: rag_performance_score_distribution?.data?.map(item => ({
                        label: `${item.range[0]}-${item.range[1]}`,
                        value: item.count
                    })) || [],
                    dateRange: responseRag.analytics.date_range
                };
                setRagPerformance(rag);
            }

            // sentiment
            const responseSentiment = await ApiService.sentiment(params);
            if (responseSentiment.success && responseSentiment.analytics) {
                const { overall_sentiment, emotion_distribution, average_sentiment_score } = responseSentiment.analytics;

                const sentimentData = {
                    overall: overall_sentiment?.map(item => ({
                        id: item.sentiment,
                        label: formatLabel(item.sentiment),
                        value: item.count
                    })) || [],
                    emotions: emotion_distribution?.map(item => ({
                        id: item.emotion,
                        label: `${getEmotionEmoji(item.emotion)} ${item.emotion}`,
                        value: item.count
                    })) || [],
                    averageScore: average_sentiment_score || 0,
                    dateRange: responseSentiment.analytics.date_range
                };
                setSentiment(sentimentData);
            }

            // query technicals
            const responseTechnicals = await ApiService.queryTechnicals(params);
            if (responseTechnicals.success && responseTechnicals.analytics) {
                const { language_distribution, question_mark_count, url_reference_count } = responseTechnicals.analytics;
                setQueryTechnicals({
                    languages: language_distribution?.map(item => ({
                        label: item.language,
                        value: item.count
                    })) || [],
                    questionMarks: question_mark_count || 0,
                    urlReferences: url_reference_count || 0,
                    dateRange: responseTechnicals.analytics.date_range
                });
            }

        } catch (error) {
            console.error('Failed to load overview:', error);
            toast.error('Failed to load reports analytics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlStartDate = params.get('start_date')?.replace(' ', '+');
        const urlEndDate = params.get('end_date')?.replace(' ', '+');

        if (urlStartDate && urlEndDate) {
            setStartDate(dayjs(urlStartDate).toDate());
            setEndDate(dayjs(urlEndDate).toDate());
            setHasFilter(true);
            const loadParams = {
                start_date: urlStartDate,
                end_date: urlEndDate
            };
            setDateParams(loadParams);
            loadOverview(loadParams);
        } else {
            loadOverview();
        }
    }, []);

    // Handle automated report generation
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const isAutomated = params.get('automated') === 'true';
        const reportType = params.get('type') || 'weekly';

        // Helper to signal failure to Playwright
        const signalFailure = (msg) => {
            console.error(`Automation Error: ${msg}`);
            if (!document.getElementById('automation-status')) {
                const statusDiv = document.createElement('div');
                statusDiv.id = 'automation-status';
                statusDiv.innerText = 'failed';
                statusDiv.setAttribute('data-error', msg);
                document.body.appendChild(statusDiv);
            }
        };

        if (isAutomated && !isAutoSending) {
            console.log('Reports: Automated mode detected', {
                loading,
                hasOverview: !!overview,
                hasSentiment: !!sentiment,
                hasRag: !!ragPerformance,
                hasUrgency: !!urgency
            });

            // Safety timeout: If data doesn't load within 60 seconds, fail the automation
            const safetyTimeout = setTimeout(() => {
                if (!overview && !loading) {
                    console.error('Reports: Automation safety timeout reached');
                    signalFailure('Data loading timed out or returned no results');
                }
            }, 60000);

            const isDataReady = overview && sentiment && ragPerformance && urgency;

            if (!loading && isDataReady) {
                clearTimeout(safetyTimeout);
                const timer = setTimeout(async () => {
                    setIsAutoSending(true);
                    try {
                        console.log('Starting automated PDF generation...');
                        const pdfBlob = await generatePDFBlob();
                        if (!pdfBlob) throw new Error('PDF generation returned empty blob');

                        console.log('PDF generated, sending to backend...');
                        const result = await ApiService.sendTestReport(pdfBlob, reportType, dateParams);

                        if (result && result.success) {
                            console.log('Automated report sent successfully');
                            const statusDiv = document.createElement('div');
                            statusDiv.id = 'automation-status';
                            statusDiv.innerText = 'success';
                            document.body.appendChild(statusDiv);
                        } else {
                            throw new Error(result?.message || 'Backend rejected the report');
                        }
                    } catch (error) {
                        signalFailure(error.message);
                    }
                }, 5000); // Give extra time for charts to animate

                return () => {
                    clearTimeout(timer);
                    clearTimeout(safetyTimeout);
                };
            }
            return () => clearTimeout(safetyTimeout);
        }
    }, [loading, overview, sentiment, ragPerformance, urgency, isAutoSending]);



    const handleDateRangeChange = async (since, until) => {
        if (!since || !until) {
            // Clear filter
            setStartDate(null);
            setEndDate(null);
            setHasFilter(false);
            await Promise.all([loadOverview()]);
            toast.success('Filter cleared');
            return;
        }

        setStartDate(since);
        setEndDate(until);
        setHasFilter(true);

        const params = {
            start_date: dayjs(since).format('YYYY-MM-DD'),
            end_date: dayjs(until).format('YYYY-MM-DD')
        };
        setDateParams(params);

        await Promise.all([loadOverview(params)]);
    };

    const handleClearFilter = async () => {
        setStartDate(null);
        setEndDate(null);
        setHasFilter(false);
        localStorage.removeItem('defaultDateRange');
        await Promise.all([loadOverview()]);
    };

    const generatePDFBlob = async () => {
        try {
            // Add style to force full text visibility and fix layout during capture
            const style = document.createElement('style');
            style.id = 'pdf-capture-style';
            style.innerHTML = `
                /* Global overrides for capture */
                * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    transition: none !important;
                    animation: none !important;
                    text-shadow: none !important;
                    box-shadow: none !important;
                    backdrop-filter: none !important;
                    filter: none !important;
                }
                
                .truncate, .truncate-none { 
                    white-space: normal !important; 
                    overflow: visible !important; 
                    text-overflow: clip !important;
                    display: block !important;
                    max-height: none !important;
                    -webkit-line-clamp: unset !important;
                }

                dt.truncate, dd.truncate {
                    white-space: normal !important;
                    overflow: visible !important;
                    display: block !important;
                }

                /* Container fix */
                #report-content {
                    width: 1100px !important;
                    background: white !important;
                    padding: 40px !important;
                    opacity: 1 !important;
                    visibility: visible !important;
                }

                /* Table layout fix for Recommendations Summary */
                #recommendations-summary-table {
                    width: 100% !important;
                    table-layout: fixed !important;
                    border-collapse: collapse !important;
                    background: white !important;
                }

                #recommendations-summary-table th, 
                #recommendations-summary-table td {
                    white-space: normal !important;
                    word-break: break-word !important;
                    padding: 12px 16px !important;
                    opacity: 1 !important;
                }

                /* Specific column widths for the table to prevent overlap */
                #recommendations-summary-table th:nth-child(1),
                #recommendations-summary-table td:nth-child(1) {
                    width: 100px !important;
                }
                #recommendations-summary-table th:nth-child(2),
                #recommendations-summary-table td:nth-child(2) {
                    width: auto !important;
                }
                #recommendations-summary-table th:nth-child(3),
                #recommendations-summary-table td:nth-child(3) {
                    width: 250px !important;
                }

                /* Ensure grid columns don't squeeze */
                .grid-cols-3 {
                    display: grid !important;
                    grid-template-columns: repeat(3, 1fr) !important;
                    gap: 1.5rem !important;
                    opacity: 1 !important;
                }
                
                .grid-cols-2 {
                    display: grid !important;
                    grid-template-columns: repeat(2, 1fr) !important;
                    gap: 1.5rem !important;
                }

                /* Force visibility of all containers */
                .bg-white, .bg-gray-50, .bg-black {
                    opacity: 1 !important;
                    visibility: visible !important;
                }

                /* Fix for Pie Charts squeezing */
                .MuiChartsPie-root {
                    width: 100% !important;
                    opacity: 1 !important;
                }
                
                /* Specific fix for Recommendations Summary gradient card overlap */
                .bg-white.rounded-2xl.p-8.border {
                    margin-top: 40px !important;
                    box-shadow: none !important;
                }
            `;
            document.head.appendChild(style);

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10; // 10mm margin
            const contentWidth = pageWidth - (2 * margin);
            const printableHeight = pageHeight - (2 * margin);

            let currentY = margin;

            // Helper to add image to PDF with auto-splitting for large images
            const addToPdf = async (element) => {
                try {
                    // Minimal delay to ensure any immediate UI updates are settled
                    await new Promise(resolve => setTimeout(resolve, 50));

                    const canvas = await html2canvas(element, {
                        scale: 2,
                        useCORS: true,
                        logging: false,
                        backgroundColor: '#ffffff',
                        allowTaint: false,
                        imageTimeout: 30000,
                        onclone: (clonedDoc) => {
                            const muiElements = clonedDoc.querySelectorAll('.MuiChartsSurface-root, svg');
                            muiElements.forEach(el => {
                                el.style.opacity = '1';
                                el.style.visibility = 'visible';
                            });
                        }
                    });

                    const imgData = canvas.toDataURL('image/png');
                    const imgProps = pdf.getImageProperties(imgData);
                    const totalImgHeight = (imgProps.height * contentWidth) / imgProps.width;

                    // If the current image can fit on the remaining part of the page
                    if ((currentY + totalImgHeight) <= (pageHeight - margin)) {
                        pdf.addImage(imgData, 'PNG', margin, currentY, contentWidth, totalImgHeight, undefined, 'FAST');
                        currentY += totalImgHeight + 5;
                    } else {
                        // If it doesn't fit, check if we should start on a new page
                        if (currentY > margin + 20 && totalImgHeight < printableHeight) {
                            pdf.addPage();
                            currentY = margin;
                            pdf.addImage(imgData, 'PNG', margin, currentY, contentWidth, totalImgHeight, undefined, 'FAST');
                            currentY += totalImgHeight + 5;
                        } else {
                            // The image is too large for the remaining space or even a whole page
                            // We need to split it across pages
                            let remainingHeight = totalImgHeight;
                            let sourceY = 0; // tracking how much of the original image we've processed

                            while (remainingHeight > 0) {
                                // If current page is mostly full, start a new one
                                if (currentY > pageHeight - margin - 30) {
                                    pdf.addPage();
                                    currentY = margin;
                                }

                                const spaceLeft = pageHeight - margin - currentY;
                                const partHeight = Math.min(remainingHeight, spaceLeft);

                                // Create a temporary canvas to slice the main canvas
                                const sliceCanvas = document.createElement('canvas');
                                sliceCanvas.width = canvas.width;
                                sliceCanvas.height = (partHeight * canvas.width) / contentWidth;

                                const ctx = sliceCanvas.getContext('2d');
                                ctx.drawImage(
                                    canvas,
                                    0, (sourceY * canvas.width) / contentWidth, canvas.width, sliceCanvas.height, // Source
                                    0, 0, sliceCanvas.width, sliceCanvas.height // Destination
                                );

                                const partImgData = sliceCanvas.toDataURL('image/png');
                                pdf.addImage(partImgData, 'PNG', margin, currentY, contentWidth, partHeight, undefined, 'FAST');

                                remainingHeight -= partHeight;
                                sourceY += partHeight;

                                if (remainingHeight > 0) {
                                    pdf.addPage();
                                    currentY = margin;
                                } else {
                                    currentY += partHeight + 5;
                                }
                            }
                        }
                    }
                } catch (err) {
                    console.error('Error processing section:', err);
                }
            };

            // Global Pre-render: Scroll over all content to trigger lazy animations/loads
            const reportContent = document.querySelector('#report-content');
            if (reportContent) {
                reportContent.scrollIntoView({ block: 'end' });
                await new Promise(resolve => setTimeout(resolve, 500));
                reportContent.scrollIntoView({ block: 'start' });
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            // Process Header
            const headerElement = document.querySelector('#report-header');
            if (headerElement) {
                await addToPdf(headerElement);
            }

            // Process Welcome Banner
            const welcomeBanner = document.querySelector('#report-content > div.bg-black.rounded-lg');
            if (welcomeBanner) {
                await addToPdf(welcomeBanner);
            }

            // Process major sections
            const sections = [];

            // Batch some sections together to reduce overhead, 
            // the splitting logic will handle page overflows anyway.
            const overviewSection = document.querySelector('#report-sections > div:nth-child(1)');
            if (overviewSection) sections.push(overviewSection);

            const takeawaysSection = document.querySelector('#report-sections > div:nth-child(2)');
            if (takeawaysSection) sections.push(takeawaysSection);

            // Keep these separate for better readability/logical breaks in PDF
            const strategicSection = document.querySelector('#report-sections > div:nth-child(3)');
            if (strategicSection) sections.push(strategicSection);

            const vocSection = document.querySelector('#report-sections > div:nth-child(4)');
            if (vocSection) sections.push(vocSection);

            const supportSection = document.querySelector('#report-sections > div:nth-child(5)');
            if (supportSection) sections.push(supportSection);

            console.log(`PDF Gen Optimized: Found ${sections.length} major sections to process`);

            for (const section of sections) {
                if (section.offsetHeight === 0) continue;
                await addToPdf(section);
            }

            // Add Page Numbers
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(8);
                pdf.setTextColor(150);
                pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
            }

            // Final PDF generation
            const pdfBlob = pdf.output('blob');
            return pdfBlob;
        } catch (error) {
            console.error('PDF Generation failed:', error);
            throw error;
        } finally {
            // Remove the temporary capture style
            const style = document.getElementById('pdf-capture-style');
            if (style) style.remove();
        }
    };

    const exportToPDF = async () => {
        try {
            setExporting(true);
            const pdfBlob = await generatePDFBlob();

            const orgName = orgData?.organization_name?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'report';
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
            const fileName = `${orgName}_uttik_${dateStr}_${timeStr}.pdf`;

            const pdfUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = fileName;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(pdfUrl);
            }, 100);
            toast.success('Report exported successfully');
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Failed to export report');
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white p-4">
                <div className="max-w-7xl mx-auto space-y-4">
                    {/* Skeleton Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-6">
                        <div>
                            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2"></div>
                            <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                        <div className="flex gap-2">
                            <div className="h-10 w-48 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                    </div>

                    <div className="space-y-6 bg-white/50 p-6 rounded-3xl">
                        {/* Status Cards Skeleton */}
                        <div>
                            <div className="flex items-center gap-4 mb-6 mt-2">
                                <div className="h-12 w-12 bg-gray-200 rounded-xl animate-pulse"></div>
                                <div>
                                    <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
                                    <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-6">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <StatusCardSkeleton key={i} />
                                ))}
                            </div>

                            {/* Daily Trend Skeleton */}
                            <LineChartSkeleton />
                        </div>

                        {/* Key Takeaways Skeleton */}
                        <div>
                            <div className="flex items-center gap-4 mb-6 mt-8">
                                <div className="h-12 w-12 bg-gray-200 rounded-xl animate-pulse"></div>
                                <div>
                                    <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
                                    <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {[1, 2, 3].map((i) => (
                                    <KeyTakeawaySkeleton key={i} />
                                ))}
                            </div>
                        </div>

                        {/* Strategic Insights Skeleton */}
                        <div>
                            <div className="flex items-center gap-4 mb-6 mt-8">
                                <div className="h-12 w-12 bg-gray-200 rounded-xl animate-pulse"></div>
                                <div>
                                    <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
                                    <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ChartSkeleton />
                                <BarChartSkeleton />
                            </div>
                            <div className="mt-6">
                                <BarChartSkeleton />
                            </div>
                            <RecommendationSkeleton />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const generateUniqueColors = (count) => {
        const colors = [
            '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
            '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#06B6D4'
        ];

        if (count <= colors.length) {
            return colors.slice(0, count);
        }

        const generatedColors = [];
        for (let i = 0; i < count; i++) {
            generatedColors.push(colors[i % colors.length]);
        }
        return generatedColors;
    };

    // Custom Tooltip for Charts
    const CustomChartTooltip = (props) => {
        const { itemData, series } = props;
        if (!itemData) return null;

        const dataItem = series.data[itemData.dataIndex];
        const id = dataItem.id;

        let description = '';
        if (DESCRIPTIONS.primary_category[id]) description = DESCRIPTIONS.primary_category[id];
        else if (DESCRIPTIONS.intent[id]) description = DESCRIPTIONS.intent[id];
        else if (DESCRIPTIONS.root_cause[id]) description = DESCRIPTIONS.root_cause[id];
        else if (DESCRIPTIONS.rag_performance[id]) description = DESCRIPTIONS.rag_performance[id];

        return (
            <Paper elevation={3} sx={{ p: 2, maxWidth: 300, bgcolor: 'rgba(255, 255, 255, 0.95)' }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                    {dataItem.label.split('|')[0].trim()}
                </Typography>
                <Typography variant="body2" sx={{ my: 1 }}>
                    Count: <strong>{dataItem.value}</strong>
                </Typography>
                {description && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, borderTop: 1, borderColor: 'divider', pt: 1, fontStyle: 'italic' }}>
                        {description}
                    </Typography>
                )}
            </Paper>
        );
    };

    const selectTitle = (selectedReport) => {
        switch (selectedReport) {
            case 'aiResponseQuality':
                return 'Ai Response Quality Details';
            case 'queryClassification':
                return 'Query Classification Details';
            case 'queryTechnicals':
                return 'Query Technical Details'
            case 'sentiment':
                return 'Sentiment Details';
            case 'urgency':
                return 'Urgency Details';
            case 'rootCause':
                return 'Root Cause Details';
            default:
                return 'Rag Performance Details';
        }
    }

    return (
        <div className="space-y-4">
            <div className="w-full space-y-4">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                        {selectedReport && (
                            <button
                                onClick={() => setSelectedReport(null)}
                                className="p-2 sm:p-2.5 bg-white hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                            >
                                <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
                            </button>
                        )}
                        <div>
                            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent leading-tight">
                                {selectedReport ? selectTitle(selectedReport) : 'Analytics Dashboard'}
                            </h1>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                {selectedReport ? 'In-depth insights and metrics' : 'Real-time performance overview'}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto z-20">
                        <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            onChange={handleDateRangeChange}
                            className="flex-1 sm:flex-initial min-w-0"
                        />

                        {hasFilter && (
                            <button
                                onClick={handleClearFilter}
                                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all whitespace-nowrap"
                            >
                                Clear Filter
                            </button>
                        )}

                        <button
                            onClick={exportToPDF}
                            disabled={exporting}
                            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm whitespace-nowrap"
                        >
                            {exporting ? (
                                <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                            )}
                            <span className="hidden sm:inline">Export PDF</span>
                            <span className="sm:hidden">Export</span>
                        </button>

                        <button
                            onClick={() => setShowSettingsModal(true)}
                            className="p-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-black transition-all shadow-sm"
                            title="Notification Settings"
                        >
                            <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                    </div>
                </div>

                {/* Date Range Summary for Mobile - Only visible when header wraps */}
                {startDate && endDate && (
                    <div className="sm:hidden flex items-center gap-2 text-[10px] font-medium text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                        <Clock className="w-3 h-3" />
                        <span>{dayjs(startDate).format('MMM DD')} – {dayjs(endDate).format('MMM DD, YYYY')}</span>
                    </div>
                )}

                {
                    !selectedReport ? (
                        <div id="report-content" className="space-y-3 sm:space-y-4 bg-white p-2 sm:p-3 rounded-2xl border border-gray-200">
                            {/* Report Header for PDF */}
                            {orgData && (
                                <ReportHeader
                                    orgData={orgData}
                                    startDate={startDate || new Date(new Date().setDate(new Date().getDate() - 30))}
                                    endDate={endDate || new Date()}
                                />
                            )}

                            {/* Welcome Banner - Specifically included in PDF by generatePDFBlob */}
                            <div className="bg-black rounded-lg sm:rounded-xl p-4 sm:p-5 text-white shadow-lg relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-48 h-48 bg-gray-800 rounded-full blur-3xl"></div>
                                <div className="relative z-10 flex items-start gap-2 sm:gap-3">
                                    <div className="p-2 sm:p-3 bg-gray-800 backdrop-blur-sm rounded-lg sm:rounded-xl">
                                        <Activity className="h-5 w-5 sm:h-6 sm:w-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h2 className="text-base sm:text-lg font-bold mb-1">Analytics Overview</h2>
                                        <p className="text-[11px] sm:text-xs text-gray-300">
                                            Comprehensive insights into AI performance, query patterns, and customer interactions
                                            {hasFilter && <span className="font-bold text-white"> for period: {startDate?.toLocaleDateString()} - {endDate?.toLocaleDateString()}</span>}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Main Content Sections */}
                            <div id="report-sections" className="space-y-3 sm:space-y-4">

                                {/* 1. High-Level Overview */}
                                <div>
                                    <SectionHeader
                                        title="High-Level Overview"
                                        description="Key metrics and real-time status at a glance."
                                        icon={Activity}
                                        color="blue"
                                    />

                                    <div className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-4 mb-3 sm:mb-4">
                                        <StatusCard
                                            title="Total Queries"
                                            value={overview?.total_queries || 0}
                                            icon={Mail}
                                            color="blue"
                                            onClick={() => navigate('/support?status=all')}
                                        />
                                        <StatusCard
                                            title="Acknowledged"
                                            value={overview?.total_queries || 0}
                                            icon={CheckCircle2}
                                            color="blue"
                                            onClick={() => navigate('/support?status=acknowledged')}
                                        />
                                        <StatusCard
                                            title="Follow-up"
                                            value={overview?.responded_queries || 0}
                                            icon={MessageSquare}
                                            color="green"
                                            onClick={() => navigate('/support?status=responded')}
                                        />
                                        <StatusCard
                                            title="Pending Follow-up"
                                            value={overview?.pending_queries || 0}
                                            icon={Clock}
                                            color="orange"
                                            onClick={() => navigate('/support?status=pending')}
                                        />
                                        <StatusCard
                                            title="Closed"
                                            value={overview?.closed_queries || 0}
                                            icon={XCircle}
                                            color="gray"
                                            onClick={() => navigate('/support?status=closed')}
                                        />
                                    </div>

                                    {/* Daily Query Trend */}
                                                <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200">
                                        <h3 className="text-base font-semibold text-gray-900 mb-3">Daily Query Trend</h3>
                                        {dailyTraffic.length > 0 ? (
                                            <div className="w-full overflow-x-auto">
                                                <LineChart
                                                    xAxis={[{
                                                        data: dailyTraffic.map(item => new Date(item.date)),
                                                        scaleType: 'time',
                                                        valueFormatter: (date) =>
                                                            new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                                    }]}
                                                    series={[{
                                                        data: dailyTraffic.map(item => item.count),
                                                        baseline: 'min',
                                                        label: 'Daily Queries',
                                                        color: '#000000',
                                                        area: true,
                                                    }]}
                                                    height={300}
                                                    grid={{ vertical: true, horizontal: true }}
                                                />
                                            </div>
                                        ) : (
                                            <EmptyChart
                                                icon={TrendingUp}
                                                title="No Query Data"
                                                description="Query data will appear here once there is activity."
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Key Takeaways & Action Items (New Section) */}
                                <KeyTakeaways
                                    insights={keyInsights}
                                    onInsightClick={(insight) => {
                                        if (insight.filterType && insight.filterValue) {
                                            handleChartClick(insight.filterType, insight.filterValue, insight.modalTitle || insight.title);
                                        }
                                    }}
                                />

                                {/* 4. Strategic Insights (Business) */}
                                <div>
                                    <SectionHeader
                                        title="Strategic & Business Insights"
                                        description="High-level data for strategic decision-making."
                                        icon={Award}
                                        color="purple"
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-4">
                                        {/* Primary Category Distribution */}
                                        {queryClassification && (
                                                        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200">
                                                <div className="flex justify-between items-center mb-4">
                                                    <div className="flex items-center">
                                                        <h3 className="text-base font-semibold text-gray-900">Primary Category Distribution</h3>
                                                        <InfoTooltip text="Distribution of queries based on their primary topic or domain." />
                                                    </div>
                                                    <SortButton
                                                        sortOrder={primaryCategorySortOrder}
                                                        onSort={() => setPrimaryCategorySortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                                    />
                                                </div>
                                                {queryClassification.primaryCategory?.length > 0 ? (
                                                    <PieChart
                                                        series={[{
                                                            data: sortChartData(queryClassification.primaryCategory, primaryCategorySortOrder).map(d => ({
                                                                ...d,
                                                                label: `${d.label || 'N/A'} | ${((d.value / queryClassification.totalQueries) * 100).toFixed(0)}% | ${d.value} Queries`
                                                            })),
                                                            highlightScope: { faded: 'global', highlighted: 'item' },
                                                        }]}
                                                        colors={generateUniqueColors(queryClassification.primaryCategory.length)}
                                                        height={300}
                                                        tooltip={{ trigger: 'item' }}
                                                        slots={{ itemContent: CustomChartTooltip }}
                                                        onItemClick={(event, d) => {
                                                            const sortedData = sortChartData(queryClassification.primaryCategory, primaryCategorySortOrder);
                                                            const clickedItem = sortedData[d.dataIndex];
                                                            handleChartClick(
                                                                'primary_category',
                                                                clickedItem.id,
                                                                `Primary Category: ${clickedItem.label.split('|')[0].trim()}`
                                                            );
                                                        }}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                ) : (
                                                    <EmptyChart icon={BarChart3} title="No Data" description="Category data will appear here" />
                                                )}
                                                <ChartFooter dateRange={queryClassification?.dateRange} />
                                            </div>
                                        )}

                                        {/* Intent Distribution */}
                                        {queryClassification && (
                                                        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200">
                                                <div className="flex justify-between items-center mb-4">
                                                    <div className="flex items-center">
                                                        <h3 className="text-base font-semibold text-gray-900">Intent Distribution</h3>
                                                        <InfoTooltip text="User intent behind the queries." />
                                                    </div>
                                                    <SortButton
                                                        sortOrder={intentSortOrder}
                                                        onSort={() => setIntentSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                                    />
                                                </div>
                                                {queryClassification.intent?.length > 0 ? (
                                                    <Stack>
                                                        <BarChart
                                                            height={window.innerWidth < 640 ? 250 : 300}
                                                            dataset={sortChartData(queryClassification.intent, intentSortOrder)}
                                                            series={[{
                                                                id: 'intent',
                                                                dataKey: 'value',
                                                                color: '#000000',
                                                            }]}
                                                            layout="horizontal"
                                                            barLabel={(v) => {
                                                                const sorted = sortChartData(queryClassification.intent, intentSortOrder);
                                                                const item = sorted[v.dataIndex];
                                                                return item ? `${item.label}: ${v.value}` : '';
                                                            }}
                                                            yAxis={[{
                                                                scaleType: 'band',
                                                                dataKey: 'label',
                                                                width: window.innerWidth < 640 ? 80 : window.innerWidth < 1024 ? 120 : 140,
                                                            }]}
                                                            slots={{
                                                                legend: PiecewiseColorLegend,
                                                                barLabel: BarLabelAtBase,
                                                                bar: BarShadedBackground,
                                                            }}
                                                            onItemClick={(event, d) => {
                                                                if (d && typeof d.dataIndex !== 'undefined') {
                                                                    const sortedData = sortChartData(queryClassification.intent, intentSortOrder);
                                                                    const clickedItem = sortedData[d.dataIndex];
                                                                    handleChartClick(
                                                                        'intent',
                                                                        clickedItem.id,
                                                                        `Intent: ${clickedItem.label}`
                                                                    );
                                                                }
                                                            }}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                    </Stack>
                                                ) : (
                                                    <EmptyChart icon={Target} title="No Data" description="Intent data will appear here" />
                                                )}
                                                <ChartFooter dateRange={queryClassification?.dateRange} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Product Area Distribution (Full Width) */}
                                    {queryClassification && queryClassification.productArea?.length > 0 && (
                                        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5 md:p-6 border border-gray-200 mt-4 sm:mt-6">
                                            <div className="flex justify-between items-center mb-4">
                                                <div className="flex items-center">
                                                    <h3 className="text-base font-semibold text-gray-900">Product Area Distribution (Top 10)</h3>
                                                    <InfoTooltip text="Distribution of queries based on product area. Top 10 shown, others grouped." />
                                                </div>
                                            </div>
                                            {queryClassification.productArea?.length > 0 ? (
                                                <Stack>
                                                    <BarChart
                                                        height={window.innerWidth < 640 ? 280 : window.innerWidth < 1024 ? 320 : 350}
                                                        dataset={getProcessedProductAreaData()}
                                                        series={[{
                                                            dataKey: 'value',
                                                            label: 'Queries',
                                                            color: '#000000',
                                                        }]}
                                                        layout="horizontal"
                                                        barLabel={(v) => {
                                                            const data = getProcessedProductAreaData();
                                                            const item = data[v.dataIndex];
                                                            return item ? `${item.value}` : '';
                                                        }}
                                                        yAxis={[{
                                                            scaleType: 'band',
                                                            dataKey: 'label',
                                                            width: window.innerWidth < 640 ? 100 : window.innerWidth < 1024 ? 140 : 180,
                                                            tickLabelStyle: {
                                                                fontSize: window.innerWidth < 640 ? 10 : 12,
                                                                fontWeight: 500,
                                                            }
                                                        }]}
                                                        slots={{
                                                            legend: PiecewiseColorLegend,
                                                            barLabel: BarLabelAtBase,
                                                            bar: BarShadedBackground,
                                                        }}
                                                        onItemClick={(event, d) => {
                                                            if (d && typeof d.dataIndex !== 'undefined') {
                                                                const data = getProcessedProductAreaData();
                                                                const item = data[d.dataIndex];
                                                                handleProductAreaClick(item);
                                                            }
                                                        }}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                </Stack>
                                            ) : (
                                                <EmptyChart icon={BarChart3} title="No Data" description="Product area data will appear here" />
                                            )}
                                            <ChartFooter dateRange={queryClassification?.dateRange} />
                                        </div>
                                    )}

                                    {/* Recommendations Summary (New Section) */}
                                    <RecommendationsSummary recommendations={keyRecommendations} />
                                </div>

                                {/* 2. Customer Insights (Marketing) */}
                                <div>
                                    <SectionHeader
                                        title="Voice of the Customer"
                                        description="Insights into customer sentiment, demographics, and feedback."
                                        icon={Heart}
                                        color="green"
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-4">
                                        {/* Sentiment Analysis */}
                                        {sentiment && (
                                                        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-base font-semibold text-gray-900">Overall Sentiment</h3>
                                                    <SortButton
                                                        sortOrder={sentimentSortOrder}
                                                        onSort={() => setSentimentSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                                    />
                                                </div>
                                                {sentiment.overall?.length > 0 ? (
                                                    <PieChart
                                                        series={[{
                                                            data: sortChartData(sentiment.overall, sentimentSortOrder).map(d => ({
                                                                ...d,
                                                                label: `${d.label} | ${((d.value / sentiment.overall.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}% | ${d.value} Queries`,
                                                            })),
                                                            highlightScope: { faded: 'global', highlighted: 'item' },
                                                        }]}
                                                        colors={generateUniqueColors(sentiment.overall.length)}
                                                        height={300}
                                                        onItemClick={(event, d) => {
                                                            const sortedData = sortChartData(sentiment.overall, sentimentSortOrder);
                                                            const clickedItem = sortedData[d.dataIndex];
                                                            handleChartClick(
                                                                'sentiment_overall',
                                                                clickedItem.id,
                                                                `Sentiment: ${clickedItem.label.split('|')[0].trim()}`
                                                            );
                                                        }}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                ) : (
                                                    <EmptyChart icon={PieChartIcon} title="No Data" description="Sentiment data will appear here" />
                                                )}
                                                <ChartFooter dateRange={sentiment?.dateRange} />
                                            </div>
                                        )}

                                        {/* Emotion Analysis */}
                                        {sentiment && (
                                                        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-base font-semibold text-gray-900">Emotion Analysis</h3>
                                                    <SortButton
                                                        sortOrder={emotionSortOrder}
                                                        onSort={() => setEmotionSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                                    />
                                                </div>
                                                {sentiment.emotions?.length > 0 ? (
                                                    <Stack>
                                                        <BarChart
                                                            height={window.innerWidth < 640 ? 250 : 300}
                                                            dataset={sortChartData(sentiment.emotions, emotionSortOrder)}
                                                            series={[{
                                                                id: 'emotion',
                                                                dataKey: 'value',
                                                                color: '#000000',
                                                            }]}
                                                            layout="horizontal"
                                                            barLabel={(v) => {
                                                                const sorted = sortChartData(sentiment.emotions, emotionSortOrder);
                                                                const item = sorted[v.dataIndex];
                                                                return item ? `${item.label}: ${v.value}` : '';
                                                            }}
                                                            yAxis={[{
                                                                scaleType: 'band',
                                                                dataKey: 'label',
                                                                width: window.innerWidth < 640 ? 70 : window.innerWidth < 1024 ? 90 : 100,
                                                            }]}
                                                            slots={{
                                                                legend: PiecewiseColorLegend,
                                                                barLabel: BarLabelAtBase,
                                                                bar: BarShadedBackground,
                                                            }}
                                                            onItemClick={(event, d) => {
                                                                if (d && typeof d.dataIndex !== 'undefined') {
                                                                    const sortedData = sortChartData(sentiment.emotions, emotionSortOrder);
                                                                    const clickedItem = sortedData[d.dataIndex];
                                                                    handleChartClick(
                                                                        'emotion',
                                                                        clickedItem.id,
                                                                        `Emotion: ${clickedItem.label}`
                                                                    );
                                                                }
                                                            }}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                    </Stack>
                                                ) : (
                                                    <EmptyChart icon={BarChart3} title="No Data" description="Emotion data will appear here" />
                                                )}
                                                <ChartFooter dateRange={sentiment?.dateRange} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Language Distribution */}
                                    <div className="bg-white mt-5 rounded-xl shadow-lg p-4 sm:p-5 md:p-6 border border-gray-200 lg:col-span-2 w-full">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-base font-semibold text-gray-900">Audience Demographics (Language)</h3>
                                            <SortButton
                                                sortOrder={languageSortOrder}
                                                onSort={() => setLanguageSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                            />
                                        </div>
                                        {queryTechnicals?.languages?.length > 0 ? (
                                            <Stack>
                                                <BarChart
                                                    height={window.innerWidth < 640 ? 250 : 300}
                                                    dataset={sortChartData(queryTechnicals.languages, languageSortOrder)}
                                                    series={[{
                                                        id: 'language',
                                                        dataKey: 'value',
                                                        color: '#000000',
                                                    }]}
                                                    layout="horizontal"
                                                    barLabel={(v) => {
                                                        const sorted = sortChartData(queryTechnicals.languages, languageSortOrder);
                                                        const item = sorted[v.dataIndex];
                                                        return item ? `${formatLabel(item.label)}: ${item.value}` : '';
                                                    }}
                                                    yAxis={[{
                                                        scaleType: 'band',
                                                        dataKey: 'label',
                                                        width: window.innerWidth < 640 ? 70 : window.innerWidth < 1024 ? 90 : 100,
                                                        valueFormatter: (value) => formatLabel(value)
                                                    }]}
                                                    slots={{
                                                        legend: PiecewiseColorLegend,
                                                        barLabel: BarLabelAtBase,
                                                        bar: BarShadedBackground,
                                                    }}
                                                />
                                            </Stack>
                                        ) : (
                                            <EmptyChart icon={Globe2} title="No Language Data" description="Language distribution will appear here" />
                                        )}
                                        <ChartFooter dateRange={queryTechnicals?.dateRange} />
                                    </div>
                                </div>

                                {/* 3. Support Performance (After-Sales) */}
                                <div>
                                    <SectionHeader
                                        title="Support Operations & Performance"
                                        description="Metrics to optimize support quality and efficiency."
                                        icon={Target}
                                        color="orange"
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-4">
                                        {/* Root Cause Analysis */}
                                        {rootCause && (
                                                        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200">
                                                <div className="flex justify-between items-center mb-4">
                                                    <div className="flex items-center">
                                                        <h3 className="text-base font-semibold text-gray-900">Root Cause Categories</h3>
                                                        <InfoTooltip text="Underlying reasons for the user's issue or inquiry." />
                                                    </div>
                                                    <SortButton
                                                        sortOrder={rootCauseSortOrder}
                                                        onSort={() => setRootCauseSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                                    />
                                                </div>
                                                {rootCause.category?.length > 0 ? (
                                                    <PieChart
                                                        series={[{
                                                            data: sortChartData(rootCause.category, rootCauseSortOrder).map(d => ({
                                                                ...d,
                                                                label: `${d.label || 'N/A'} | ${((d.value / rootCause.category.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}% | ${d.value} Queries`
                                                            })),
                                                            highlightScope: { faded: 'global', highlighted: 'item' },
                                                        }]}
                                                        colors={generateUniqueColors(rootCause.category.length)}
                                                        height={300}
                                                        tooltip={{ trigger: 'item' }}
                                                        slots={{ itemContent: CustomChartTooltip }}
                                                        onItemClick={(event, d) => {
                                                            const sortedData = sortChartData(rootCause.category, rootCauseSortOrder);
                                                            const clickedItem = sortedData[d.dataIndex];
                                                            handleChartClick(
                                                                'root_cause_category',
                                                                clickedItem.id,
                                                                `Root Cause: ${clickedItem.label.split('|')[0].trim()}`
                                                            );
                                                        }}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                ) : (
                                                    <EmptyChart icon={BarChart3} title="No Data" description="Root cause data will appear here" />
                                                )}
                                                <ChartFooter dateRange={rootCause?.dateRange} />
                                            </div>
                                        )}

                                        {/* Knowledge Gaps */}
                                        {ragPerformance && (
                                                        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200">
                                                <div className="flex justify-between items-center mb-4">
                                                    <div className="flex items-center">
                                                        <h3 className="text-base font-semibold text-gray-900">Knowledge Gaps</h3>
                                                        <InfoTooltip text="Areas where the AI lacked sufficient knowledge to answer." />
                                                    </div>
                                                    <SortButton
                                                        sortOrder={ragGapSortOrder}
                                                        onSort={() => setRagGapSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                                    />
                                                </div>
                                                {ragPerformance.knowledgeGaps?.length > 0 ? (
                                                    <Stack>
                                                        <BarChart
                                                            height={window.innerWidth < 640 ? 250 : 300}
                                                            dataset={sortChartData(ragPerformance.knowledgeGaps, ragGapSortOrder)}
                                                            series={[{
                                                                id: 'gap',
                                                                dataKey: 'value',
                                                                color: '#000000',
                                                            }]}
                                                            layout="horizontal"
                                                            barLabel={(v) => {
                                                                const sorted = sortChartData(ragPerformance.knowledgeGaps, ragGapSortOrder);
                                                                const item = sorted[v.dataIndex];
                                                                return item ? `${item.label}: ${v.value}` : '';
                                                            }}
                                                            yAxis={[{
                                                                scaleType: 'band',
                                                                dataKey: 'label',
                                                                width: window.innerWidth < 640 ? 80 : window.innerWidth < 1024 ? 110 : 140,
                                                            }]}
                                                            slots={{
                                                                legend: PiecewiseColorLegend,
                                                                barLabel: BarLabelAtBase,
                                                                bar: BarShadedBackground,
                                                            }}
                                                            onItemClick={(event, d) => {
                                                                if (d && typeof d.dataIndex !== 'undefined') {
                                                                    const sortedData = sortChartData(ragPerformance.knowledgeGaps, ragGapSortOrder);
                                                                    const clickedItem = sortedData[d.dataIndex];
                                                                    handleChartClick(
                                                                        'knowledge_gap',
                                                                        clickedItem.id,
                                                                        `Knowledge Gap: ${clickedItem.label}`
                                                                    );
                                                                }
                                                            }}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                    </Stack>
                                                ) : (
                                                    <EmptyChart icon={Database} title="No Data" description="Knowledge gap data will appear here" />
                                                )}
                                                <ChartFooter dateRange={ragPerformance?.dateRange} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Urgency Analysis */}
                                    {urgency && (
                                        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-4 mt-5">
                                                        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-200">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-base font-semibold text-gray-900">Urgency Level Distribution</h3>
                                                    <SortButton
                                                        sortOrder={urgencySortOrder}
                                                        onSort={() => setUrgencySortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                                    />
                                                </div>
                                                {urgency.level?.length > 0 ? (
                                                    <PieChart
                                                        series={[{
                                                            data: sortChartData(urgency.level, urgencySortOrder).map(d => ({
                                                                ...d,
                                                                label: `${d.label || 'N/A'} | ${((d.value / urgency.level.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}% | ${d.value} Queries`
                                                            })),
                                                            highlightScope: { faded: 'global', highlighted: 'item' },
                                                        }]}
                                                        colors={generateUniqueColors(urgency.level.length)}
                                                        height={300}
                                                        onItemClick={(event, d) => {
                                                            const sortedData = sortChartData(urgency.level, urgencySortOrder);
                                                            const clickedItem = sortedData[d.dataIndex];
                                                            handleChartClick(
                                                                'urgency_level',
                                                                clickedItem.id,
                                                                `Urgency: ${clickedItem.label.split('|')[0].trim()}`
                                                            );
                                                        }}
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                ) : (
                                                    <EmptyChart icon={PieChartIcon} title="No Data" description="Urgency data will appear here" />
                                                )}
                                                <ChartFooter dateRange={urgency?.dateRange} />
                                            </div>

                                            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-base font-semibold text-gray-900">Business Impact</h3>
                                                    <SortButton
                                                        sortOrder={impactSortOrder}
                                                        onSort={() => setImpactSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                                    />
                                                </div>
                                                {urgency.businessImpact?.length > 0 ? (
                                                    <Stack>
                                                        <BarChart
                                                            height={300}
                                                            dataset={sortChartData(urgency.businessImpact, impactSortOrder)}
                                                            series={[{
                                                                id: 'impact',
                                                                dataKey: 'value',
                                                                color: '#000000',
                                                            }]}
                                                            layout="horizontal"
                                                            barLabel={(v) => {
                                                                const sorted = sortChartData(urgency.businessImpact, impactSortOrder);
                                                                const item = sorted[v.dataIndex];
                                                                return item ? `${item.label}: ${v.value}` : '';
                                                            }}
                                                            yAxis={[{
                                                                scaleType: 'band',
                                                                dataKey: 'label',
                                                                width: 140,
                                                            }]}
                                                            slots={{
                                                                legend: PiecewiseColorLegend,
                                                                barLabel: BarLabelAtBase,
                                                                bar: BarShadedBackground,
                                                            }}
                                                            onItemClick={(event, d) => {
                                                                if (d && typeof d.dataIndex !== 'undefined') {
                                                                    const sortedData = sortChartData(urgency.businessImpact, impactSortOrder);
                                                                    const clickedItem = sortedData[d.dataIndex];
                                                                    handleChartClick(
                                                                        'business_impact',
                                                                        clickedItem.id,
                                                                        `Business Impact: ${clickedItem.label}`
                                                                    );
                                                                }
                                                            }}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                    </Stack>
                                                ) : (
                                                    <EmptyChart icon={BarChart3} title="No Data" description="Impact data will appear here" />
                                                )}
                                                <ChartFooter dateRange={urgency?.dateRange} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <RenderDetailView selectedReport={selectedReport} hasFilter={hasFilter} params={dateParams} />
                        </div>
                    )
                }
            </div>
            {/* Query Preview Modal */}
            <QueryPreviewModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                queries={modalQueries}
                title={modalTitle}
                filterType={modalFilterType}
                filterValue={modalFilterValue}
                loading={modalLoading}
                pagination={modalPagination}
                onPageChange={handleModalPageChange}
            />
            <CategoryBreakdownModal
                isOpen={categoryModalOpen}
                onClose={() => setCategoryModalOpen(false)}
                title={categoryModalTitle}
                data={categoryModalData}
                onItemClick={(item) => {
                    setCategoryModalOpen(false);
                    handleChartClick('product_area', item.label, `Product Area: ${item.label}`);
                }}
            />

            <ReportSettingsModal
                isOpen={showSettingsModal}
                onClose={() => setShowSettingsModal(false)}
                generatePDF={generatePDFBlob}
                onDateRangeChange={handleDateRangeChange}
                currentStartDate={startDate}
                currentEndDate={endDate}
            />
        </div>
    );
}

export default Reports;
